/**
 * `userPerks/{id}` — premios y ventajas por usuario con fechas (plan 2.1 §2; Gabor 4/11: «construye el
 * premio, vamos a tener más en el futuro»). Solo Functions escribe; el usuario lee las suyas.
 *
 *   free_shipping   — el comprador no paga el envío; nuvvu paga la etiqueta (`orders.perk_subsidy`).
 *   free_protection — el comprador no paga la protección (5 % + fija) y el pedido SIGUE protegido.
 *   promo_credit    — saldo de premio no retirable (bolsa `promo`, segunda fase); `amount` obligatorio.
 *
 * Vale entre `starts_at` (incluido) y `ends_at` (excluido); `revoked_at` la anula antes de tiempo.
 * Los premios del concurso de noviembre: del 1/12 al 31/12 incluido (`ends_at` = 1/1 00:00).
 */
import { z } from 'zod';
import {
  MoneySchema,
  TimestampSchema,
  UserIdSchema,
  type Currency,
  type Money,
} from './primitives.js';

export const PERK_TYPES = ['free_shipping', 'free_protection', 'promo_credit'] as const;
export const PerkTypeSchema = z.enum(PERK_TYPES);
export type PerkType = z.infer<typeof PerkTypeSchema>;

/** De dónde viene la ventaja; decide el texto que ve el usuario («premio del concurso», «Club»…). */
export const PERK_SOURCES = ['contest', 'club', 'admin', 'compensation', 'cashback'] as const;
export const PerkSourceSchema = z.enum(PERK_SOURCES);
export type PerkSource = z.infer<typeof PerkSourceSchema>;

/**
 * Saldo ya ganado que se gasta siempre, aunque el interruptor «Premios» esté apagado: puntos del
 * Club (§7.8) y cashback de campañas (se ganó con una compra).
 */
export const ALWAYS_SPENDABLE_PERK_SOURCES: readonly PerkSource[] = ['club', 'cashback'];

export const UserPerkSchema = z
  .object({
    id: z.string().min(1).max(128),
    uid: UserIdSchema,
    type: PerkTypeSchema,
    source: PerkSourceSchema,
    /** Texto corto para el usuario, en español (se traduce al enseñarlo); p. ej. «Concurso de noviembre». */
    label: z.string().min(1).max(120),
    /** Solo `promo_credit`: importe concedido. */
    amount: MoneySchema.nullable(),
    /**
     * Solo `promo_credit`: lo que queda por gastar (misma moneda que `amount`). Solo lo cambia el
     * servidor dentro de la transacción del pedido (`promoMoves`).
     */
    remaining: MoneySchema.nullable().optional(),
    starts_at: TimestampSchema,
    ends_at: TimestampSchema,
    revoked_at: TimestampSchema.nullable(),
    /** Agrupa las ventajas de una misma entrega (p. ej. `contest-2026-11`), para listar y revocar juntas. */
    batch: z.string().max(80).nullable(),
    created_by: UserIdSchema,
    reason: z.string().max(500).nullable(),
    created_at: TimestampSchema,
  })
  .superRefine((p, ctx) => {
    if (p.type === 'promo_credit' && (!p.amount || p.amount.amount <= 0))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message: 'userPerks: un saldo de premio necesita importe',
      });
    if (p.type !== 'promo_credit' && p.amount !== null)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount'],
        message: 'userPerks: solo el saldo de premio lleva importe',
      });
    if (p.ends_at.toMillis() <= p.starts_at.toMillis())
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ends_at'],
        message: 'userPerks: el final tiene que ser posterior al inicio',
      });
  });
export type UserPerk = z.infer<typeof UserPerkSchema>;
export const userPerkPath = (id: string) => `userPerks/${id}` as const;

/** Lo mínimo para decidir si una ventaja vale ahora (sirve con Timestamps o con milisegundos). */
export interface PerkWindow {
  type: PerkType;
  starts_at_ms: number;
  ends_at_ms: number;
  revoked: boolean;
}

export function perkActive(p: PerkWindow, nowMs: number): boolean {
  return !p.revoked && nowMs >= p.starts_at_ms && nowMs < p.ends_at_ms;
}

/** Ventajas del checkout que valen ahora. */
export interface CheckoutPerks {
  free_shipping: boolean;
  free_protection: boolean;
}

// ---------- saldo de premio (`promo_credit`) ----------

/** Un lote de saldo de premio: lo que queda, en su moneda, con su ventana. */
export interface PromoLot extends PerkWindow {
  id: string;
  remaining: Money;
}

/** Saldo de premio disponible ahora en una moneda (los lotes de otra moneda no cuentan). */
export function promoAvailable(
  lots: readonly PromoLot[],
  currency: Currency,
  nowMs: number,
): Money {
  const amount = lots
    .filter((l) => l.remaining.currency === currency && perkActive(l, nowMs))
    .reduce((s, l) => s + Math.max(0, l.remaining.amount), 0);
  return { amount, currency };
}

/**
 * Reparte `amount` entre los lotes válidos: primero el que caduca antes (para que no se pierda).
 * Devuelve lo que se toma de cada lote; lanza si no alcanza.
 */
export function allocatePromo(
  lots: readonly PromoLot[],
  amount: Money,
  nowMs: number,
): { id: string; amount: Money }[] {
  const usable = lots
    .filter((l) => l.remaining.currency === amount.currency && perkActive(l, nowMs))
    .filter((l) => l.remaining.amount > 0)
    .sort((a, b) => a.ends_at_ms - b.ends_at_ms || a.id.localeCompare(b.id));
  let left = amount.amount;
  const out: { id: string; amount: Money }[] = [];
  for (const l of usable) {
    if (left <= 0) break;
    const take = Math.min(left, l.remaining.amount);
    out.push({ id: l.id, amount: { amount: take, currency: amount.currency } });
    left -= take;
  }
  if (left > 0) throw new RangeError('allocatePromo: saldo de premio insuficiente');
  return out;
}

export function checkoutPerks(perks: readonly PerkWindow[], nowMs: number): CheckoutPerks {
  const on = perks.filter((p) => perkActive(p, nowMs));
  return {
    free_shipping: on.some((p) => p.type === 'free_shipping'),
    free_protection: on.some((p) => p.type === 'free_protection'),
  };
}
